import { useEffect, useRef } from 'react'
import styles from './FilterRail.module.css'
import FilterGroupView from './FilterGroupView'
import RatingsPanel from './RatingsPanel'
import type { CategoryFilterState } from 'lib/dealhunt/filters'
import type { CategoryFilterModel, CategorySummary } from 'lib/dealhunt/types'

interface FilterRailProps {
  model: CategoryFilterModel
  summary: CategorySummary
  filters: CategoryFilterState
  onChange: (next: CategoryFilterState) => void
  onOpenAll: () => void
  /** Whether the "All filters" drawer this door opens is currently open. */
  allFiltersOpen?: boolean
  /** Hover/focus on the "All filters" door: warm the deferred drawer model. */
  onPrefetchAll?: () => void
}

/** Desktop rail: SoundGuys ratings → featured groups → the door to everything else. */
const FilterRail: React.VFC<FilterRailProps> = ({
  model,
  summary,
  filters,
  onChange,
  onOpenAll,
  allFiltersOpen = false,
  onPrefetchAll,
}) => {
  // The rail has NO inner scrollbar (design: one scrollbar on the page, none
  // inside it). To keep the lower groups reachable when the rail is taller than
  // the viewport, its measured height feeds the sticky `top` in CSS: the page
  // scroll walks through the whole rail, which pins only once its bottom is in
  // view. ResizeObserver tracks height changes (facets loading, groups toggling).
  const railRef = useRef<HTMLElement>(null)
  useEffect(() => {
    const el = railRef.current
    if (!el || typeof ResizeObserver === 'undefined') {
      return undefined
    }
    const observer = new ResizeObserver(() => {
      el.style.setProperty('--rail-h', `${el.offsetHeight}px`)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <aside ref={railRef} className={styles.rail} aria-label="Filters">
      <RatingsPanel model={model} summary={summary} filters={filters} onChange={onChange} />
      {model.featured.map(group => (
        <FilterGroupView key={group.id} group={group} filters={filters} onChange={onChange} />
      ))}
      {summary.moreTotal > 0 && (
        <button
          type="button"
          className={styles.allFilters}
          aria-haspopup="dialog"
          aria-expanded={allFiltersOpen}
          onClick={onOpenAll}
          onPointerEnter={onPrefetchAll}
          onFocus={onPrefetchAll}
        >
          <span>{'All filters'}</span>
          <span className={styles.allFiltersNote}>
            {`${summary.moreTotal} more · every value counted`}
          </span>
        </button>
      )}
    </aside>
  )
}

export default FilterRail
